import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { listBdcSkus, updateBdcSku, updateBdcSkuPcsPerTray } from "../../../services";
import { BdcSku } from "../../../types/bdc.type";
import { BDC_PCS_PER_CASE_MAX, BDC_PCS_PER_TRAY_MAX } from "../constants";
import { baht, rowsOf } from "../format";
import { SCROLL_SX } from "../styles";
import Tag from "../Tag";
import ProductPicker, { ProductOption } from "./ProductPicker";
import { skuTrayUpdate, skuUpdateBody } from "./updateBody";

/** The SKU's linked GB product as a picker option; the link is by product code (no uuid). */
const optionOf = (sku: BdcSku): ProductOption | null =>
  sku.product_code ? { uuid: "", code: sku.product_code, name: sku.product_name ?? "" } : null;

function SkuRow({ sku, canWrite, onSaved }: Readonly<{ sku: BdcSku; canWrite: boolean; onSaved: () => void }>) {
  const [perCase, setPerCase] = useState(String(sku.pcs_per_case));
  const [product, setProduct] = useState<ProductOption | null>(optionOf(sku));
  const [saving, setSaving] = useState(false);

  const perCaseNumber = Number(perCase);
  const perCaseValid =
    Number.isInteger(perCaseNumber) && perCaseNumber > 0 && perCaseNumber <= BDC_PCS_PER_CASE_MAX;
  const [perTray, setPerTray] = useState(sku.pcs_per_tray == null ? "" : String(sku.pcs_per_tray));
  const perTrayNumber = Number(perTray);
  // an API without pcs_per_tray (older backend) leaves the column read-only
  const hasTray = sku.pcs_per_tray != null;
  const perTrayValid =
    !hasTray || (Number.isInteger(perTrayNumber) && perTrayNumber > 0 && perTrayNumber <= BDC_PCS_PER_TRAY_MAX);
  const body = skuUpdateBody(sku, perCaseValid ? perCaseNumber : null, product?.code ?? null);
  const trayUpdate = hasTray ? skuTrayUpdate(sku, perTrayValid ? perTrayNumber : null) : null;
  const dirty = Object.keys(body).length > 0 || trayUpdate !== null;

  const save = async () => {
    setSaving(true);
    try {
      // two endpoints: PATCH /bdc/skus (case, GB link) and PUT …/pcs-per-tray
      if (Object.keys(body).length > 0) await updateBdcSku(sku.barcode, body);
      if (trayUpdate !== null) await updateBdcSkuPcsPerTray(sku.barcode, trayUpdate);
      onSaved();
    } catch {
      // interceptor shows the reason (422 = ไม่พบสินค้า GB รหัสนี้)
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td className="mono">{sku.item_code}</td>
      <td className="mono">{sku.barcode}</td>
      <td>
        {sku.name}
        {sku.size && <span className="sub"> · {sku.size}</span>}
      </td>
      <td className="n">{sku.unit_price != null ? baht(sku.unit_price) : "-"}</td>
      <td>
        {canWrite ? (
          <TextField
            size="small"
            type="number"
            value={perCase}
            onChange={(e) => setPerCase(e.target.value)}
            error={!perCaseValid}
            helperText={perCaseValid ? undefined : `1–${BDC_PCS_PER_CASE_MAX}`}
            sx={{ width: 96 }}
            slotProps={{ htmlInput: { min: 1, max: BDC_PCS_PER_CASE_MAX, step: 1, style: { textAlign: "right" } } }}
          />
        ) : (
          sku.pcs_per_case
        )}
      </td>
      <td>
        {canWrite && hasTray ? (
          <TextField
            size="small"
            type="number"
            value={perTray}
            onChange={(e) => setPerTray(e.target.value)}
            error={!perTrayValid}
            helperText={perTrayValid ? undefined : `1–${BDC_PCS_PER_TRAY_MAX}`}
            sx={{ width: 96 }}
            slotProps={{
              htmlInput: {
                min: 1,
                max: BDC_PCS_PER_TRAY_MAX,
                step: 1,
                style: { textAlign: "right" },
                "aria-label": `ชิ้นต่อถาด ${sku.name}`,
              },
            }}
          />
        ) : (
          sku.pcs_per_tray ?? "-"
        )}
      </td>
      <td>
        {canWrite ? (
          <ProductPicker
            value={product}
            initialQuery={sku.barcode}
            identity="code"
            onChange={setProduct}
            disabled={saving}
          />
        ) : sku.product_code ? (
          <Tag tone="ok">
            {sku.product_code} {sku.product_name}
          </Tag>
        ) : (
          <Tag tone="warn">ยังไม่จับคู่</Tag>
        )}
      </td>
      {canWrite && (
        <td className="c">
          <Button size="small" variant="contained" disabled={!dirty || !perCaseValid || !perTrayValid} loading={saving} onClick={save}>
            บันทึก
          </Button>
        </td>
      )}
    </tr>
  );
}

/**
 * สินค้า — the 7-11 SKUs seen in ORDS: pieces per case (for ลังเต็ม/เศษ of the control sheet),
 * pieces per tray (ถาด/ชิ้น of the ใบจ่ายสินค้า) and the GB product link.
 */
export default function SkuTab({ canWrite }: Readonly<{ canWrite: boolean }>) {
  const [skus, setSkus] = useState<BdcSku[]>([]);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await listBdcSkus();
      setSkus(rowsOf(data).slice().sort((a, b) => a.item_code.localeCompare(b.item_code)));
      setVersion((v) => v + 1);
    } catch {
      setSkus([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const unmatched = skus.filter((sku) => !sku.product_code).length;

  return (
    <Box>
      <Typography className="sub" sx={{ px: 2, py: 1.25 }}>
        {skus.length} SKU · ยังไม่จับคู่สินค้า GB {unmatched} · ชิ้นต่อลังใช้คำนวณลังเต็ม/เศษในใบคุม (ค่าเริ่มต้น โมจิ 50 · อื่น ๆ 30) · ชิ้นต่อถาดใช้กับใบจ่ายสินค้า (โมจิ ไส้ถั่วแดง ไส้เผือก สังขยา ไส้กรอก 50 · อื่น ๆ 40)
      </Typography>
      {loading && <LinearProgress />}
      <Box sx={SCROLL_SX}>
        <table className="bdc-t">
          <thead>
            <tr>
              <th>รหัส 7-11</th>
              <th>Barcode</th>
              <th>ชื่อในไฟล์</th>
              <th className="n">ราคา/ชิ้น</th>
              <th>ชิ้นต่อลัง</th>
              <th>ชิ้นต่อถาด</th>
              <th>สินค้า GB</th>
              {canWrite && <th />}
            </tr>
          </thead>
          <tbody>
            {skus.length === 0 && !loading ? (
              <tr>
                <td colSpan={8} className="c sub">
                  ยังไม่มีสินค้า — นำเข้าไฟล์ก่อน
                </td>
              </tr>
            ) : (
              skus.map((sku) => (
                <SkuRow key={`${sku.barcode}-${version}`} sku={sku} canWrite={canWrite} onSaved={load} />
              ))
            )}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}
